import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {zoomLevel} from "zoom-level";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../Firebase/firebase";
import SEO from "../components/SEO";

export default function FullPattiChart() {
    const [draws, setDraws] = useState([]);
    const [drawHeading, setDrawHeading] = useState([]);
    const {drawName, jodi, pattern} = useParams();
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [drawInfo, setDrawInfo] = useState({ single: "", panel: "", jodi: "" });

    const groupDrawsByWeek = (drawsData) => {
        const groupedDraws = {};

        // Get maximum allowed days based on draw name
        const getMaxDays = () => {
            const fiveDaysDraws = ["rajdhani-night", "tara-mumbai-night", "kalyan-night", "main-bazar"];
            const sixDaysDraws = [
                "good-morning", "bombay-exp", "golden", "kalyan", "goldstar", "golden-night",
                "time-bazar", "tara-mumbai-day", "milan-day", "rajdhani-day", "milan-night",
                "night-time-bazar", "madhur-night", "mahaveer", "mahakal"
            ];

            if (fiveDaysDraws.includes(drawName)) return 5;
            if (sixDaysDraws.includes(drawName)) return 6;
            return 7;
        };

        const maxDays = getMaxDays();

        // Group draws by week
        drawsData.forEach((draw) => {
            const weekStart = new Date(draw.sortDate);
            weekStart.setDate(weekStart.getDate() - (weekStart.getDay() - 1 + 7) % 7);

            const weekStartString = weekStart.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "numeric",
                year: "numeric",
            });

            if (!groupedDraws[weekStartString]) {
                groupedDraws[weekStartString] = [];
            }

            // Only add draw if we haven't reached max days for this draw type
            if (groupedDraws[weekStartString].length < maxDays) {
                groupedDraws[weekStartString].push(draw);
            }
        });

        // Process each week
        Object.keys(groupedDraws).forEach((key) => {
            const newDraws = [];
            let currentDay = 0;

            groupedDraws[key].forEach((draw) => {
                const drawDay = customDay(draw.sortDate);

                // Only add draws for valid operating days
                if (drawDay < maxDays) {
                    // Fill gaps with empty slots if needed
                    while (currentDay < drawDay) {
                        newDraws.push({
                            isEmpty: true,
                            sortDate: new Date()
                        });
                        currentDay++;
                    }
                    newDraws.push(draw);
                    currentDay = drawDay + 1;
                }
            });

            groupedDraws[key] = newDraws;
        });

        return groupedDraws;
    };

    const customDay = (date) => {
        const dayIndex = date.getDay(); // This returns 0 for Sunday, 1 for Monday, ..., 6 for Saturday
        return dayIndex === 0 ? 6 : dayIndex - 1; // Adjust Sunday to be at index 6 instead of 0
    };

    const searchMumbaiPattern = async () => {
        try {
            const patternType = pattern === "d" ? "delhiPattern" : "mumbaiPattern";
            const response = await fetch(`https://dp-anna-api.web.app/api/searchPatternDraw?drawName=${drawName}&pattern=${patternType}`);
            const result = await response.json();

            if (result.success) {
                const { drawDetails, draws } = result.data;

                if (drawDetails) {
                    setStartTime(drawDetails.startTime);
                    setEndTime(drawDetails.closingTime);
                    setDrawHeading(drawDetails.drawName);
                }

                // Transform the dates to Date objects for grouping
                const drawsWithDates = draws.map(draw => ({
                    ...draw,
                    sortDate: new Date(draw.date.split('/').reverse().join('-')) // Convert DD/MM/YYYY to YYYY-MM-DD
                }));

                setDraws(drawsWithDates);
            } else {
                console.error("API call failed:", result.error);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    // Add a new useEffect to log the times for debugging
    useEffect(() => {
        console.log('Times updated:', { startTime, endTime, drawHeading });
    }, [startTime, endTime, drawHeading]);

    useEffect(() => {
        searchMumbaiPattern();
    }, [drawName, pattern]);

    const groupedDraws = groupDrawsByWeek(draws);

    function getEndOfWeek(startDateString) {
        // Convert the input string to a Date object
        const [day, month, year] = startDateString.split('/').map(Number);
        const start = new Date(year, month - 1, day);

        // Check if the conversion is successful
        if (isNaN(start.getTime())) {
            return "Invalid date";
        }

        const end = new Date(start);

        // Get maximum days based on draw name
        const getMaxDays = () => {
            const fiveDaysDraws = ["rajdhani-night", "tara-mumbai-night", "kalyan-night", "main-bazar"];
            const sixDaysDraws = [
                "good-morning", "bombay-exp", "golden", "kalyan", "goldstar", "golden-night",
                "time-bazar", "tara-mumbai-day", "milan-day", "rajdhani-day", "milan-night",
                "night-time-bazar", "madhur-night", "mahaveer", "mahakal"
            ];

            if (fiveDaysDraws.includes(drawName)) return 4; // 5 days including start day
            if (sixDaysDraws.includes(drawName)) return 5; // 6 days including start day
            return 6; // 7 days including start day
        };

        // Add the appropriate number of days based on draw type
        end.setDate(start.getDate() + getMaxDays());

        // Format the end date as DD/MM/YYYY
        const endString = `${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}/${end.getFullYear()}`;

        return endString;
    }

    useEffect(() => {
        window.scrollTo(0, document.body.scrollHeight);
    }, [groupedDraws]);

    zoomLevel()

    let fiveDaysReturn = {
        mainDiv: "grid grid-cols-6",
        innerDiv: "col-span-6 grid grid-cols-6",
        daysArray: ["Date", "Mon", "Tue", "Wed", "Thu", "Fri"]
    };

    let sixDaysReturn = {
        mainDiv: "grid grid-cols-7",
        innerDiv: "col-span-7 grid grid-cols-7",
        daysArray: ["Date", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    };

    let sevenDaysReturn = {
        mainDiv: "grid grid-cols-8",
        innerDiv: "col-span-8 grid grid-cols-8",
        daysArray: ["Date", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    };

    const numberOfWeeksToShowAndDaysToShow = () => {
        const sixDaysReturnDraws = [
            "good-morning", "bombay-exp", "golden", "kalyan", "goldstar", "golden-night",
            "time-bazar", "tara-mumbai-day", "milan-day", "rajdhani-day", "milan-night",
            "night-time-bazar", "madhur-night", "mahaveer", "mahakal"
        ];

        const sevenDaysReturnDraws = [
            "sridevi", "madhur-morning", "madhur-day", "sridevi-night", "goa-golden"
        ];

        const fiveDaysReturnDraws = [
            "rajdhani-night", "tara-mumbai-night", "kalyan-night", "main-bazar"
        ];

        if (sixDaysReturnDraws.includes(drawName)) {
            return {...sixDaysReturn, daysArray: sixDaysReturn.daysArray.slice(0, 7)};
        } else if (sevenDaysReturnDraws.includes(drawName)) {
            return sevenDaysReturn;
        } else if (fiveDaysReturnDraws.includes(drawName)) {
            return {...fiveDaysReturn, daysArray: fiveDaysReturn.daysArray.slice(0, 6)};
        } else {
            return null; // Handle the case where drawName does not match any case
        }
    };

    const hideColumnsForDraw = (drawName, drawIndex) => {
        const hideCasesByIndex = {
            7: ["good-morning", "bombay-exp", "golden", "kalyan", "goldstar", "golden-night",
                "time-bazar", "tara-mumbai-day", "milan-day", "rajdhani-day", "goa-golden",
                "night-time-bazar", "mahaveer", "mahakal"],
            8: ["sridevi", "madhur-morning", "madhur-day", "sridevi-night"],
            6: ["tara-mumbai-night", "milan-night", "madhur-night"],
            5: ["rajdhani-night", "main-bazar", "kalyan-night"]
        };

        return hideCasesByIndex[drawIndex] && hideCasesByIndex[drawIndex].includes(drawName)
            ? 'hidden'
            : '';
    };

    const fetchDrawInfo = async () => {
        try {
            const drawRef = doc(db, 'mumbai-pattern-draws', drawName);
            const drawSnapshot = await getDoc(drawRef);

            if (drawSnapshot.exists()) {
                const data = drawSnapshot.data();
                setDrawInfo({
                    single: data.single || "",
                    panel: data.panel || "",
                    jodi: data.jodi || ""
                });
            }
        } catch (error) {
            console.error("Error fetching draw info:", error);
        }
    };

    useEffect(() => {
        fetchDrawInfo();
    }, [drawName]);

    const convertTo12Hour = (time) => {
        if (!time) return "";
        const [hours, minutes] = time.split(":");
        const formattedHours = hours.padStart(2, '0');
        const formattedMinutes = minutes.padStart(2, '0');
        const period = formattedHours >= 12 ? 'PM' : 'AM';
        const displayHours = formattedHours > 12 ? formattedHours - 12 : formattedHours === "00" ? "12" : formattedHours;
        return `${displayHours}:${formattedMinutes} ${period}`;
    };

    const createChartSchema = () => {
        return {
            "@context": "https://schema.org",
            "@type": "Table",
            "about": `${drawHeading} Chart Results`,
            "name": `${drawHeading} Results Chart`,
            "description": `Historical results and patterns for ${drawHeading} draw`
        };
    };

    return (
        <div className={`mx-auto max-w-[100vw] min-w-[350px] lg:min-w-[1300px] bg-[#ffcc99]`}>
            <SEO 
                title={`${drawHeading || drawName} Chart`}
                description={`View the complete chart history for ${drawHeading || drawName}. Check all historical numbers, patterns and results.`}
                keywords={`${drawHeading}, ${drawName}, chart, patti, results, history, panel`}
                schema={createChartSchema()}
            />
            {/* Sticky header container */}
            <div className="sticky top-0 z-50">
                {/* Enhanced top bar with animated gradient */}
                <div className="bg-gradient-to-r from-[#8B4513] via-[#5b3410] to-[#8B4513] shadow-lg border-b-4 border-[#FFD700] animate-gradient-x">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex space-x-2 p-1">
                            {/* Left-aligned image */}
                            <div className="w-32 md:w-36 lg:w-40 flex-shrink-0">
                                <img
                                    src={require("../Images/ganesh-lakshmi_1508345551.avif")}
                                    alt="Ganesh Lakshmi"
                                    className="w-full h-auto rounded-lg border-2 border-[#ffcc99]/30"
                                />
                            </div>
                
                            {/* Title */}
                            <div className="flex flex-col flex-grow">
                                <div className="flex-shrink-0 w-full">
                                    <div className="border-2 border-[#FFD700] shadow-lg overflow-hidden px-2 py-1 rounded-xl bg-gradient-to-b from-[#5b3410] to-[#3b2208] relative">
                                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjZmZkNzAwMjAiPjwvcmVjdD4KPHBhdGggZD0iTTAgNUw1IDBaTTYgNEw0IDZaTS0xIDFMMSAtMVoiIHN0cm9rZT0iI2ZmZDcwMDEwIiBzdHJva2Utd2lkdGg9IjEiPjwvcGF0aD4KPC9zdmc+')] opacity-20"></div>
                                        <h1 className="flex text-base md:text-xl lg:text-2xl font-black uppercase tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] via-[#FDB931] to-[#FFD700] drop-shadow-[0_2px_2px_rgba(0,0,0,0.8)] relative z-10 text-center items-center justify-center leading-tight min-h-[24px]">
                                            {drawHeading}
                                        </h1>
                                    </div>
                                </div>
                
                                {/* Time Section */}
                                <div className="w-full border-2 border-[#FFD700] shadow-lg overflow-hidden px-2 py-1 rounded-xl bg-gradient-to-b from-[#5b3410] to-[#3b2208] relative mt-1">
                                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1IiBoZWlnaHQ9IjUiPgo8cmVjdCB3aWR0aD0iNSIgaGVpZ2h0PSI1IiBmaWxsPSIjZmZkNzAwMjAiPjwvcmVjdD4KPHBhdGggZD0iTTAgNUw1IDBaTTYgNEw0IDZaTS0xIDFMMSAtMVoiIHN0cm9rZT0iI2ZmZDcwMDEwIiBzdHJva2Utd2lkdGg9IjEiPjwvcGF0aD4KPC9zdmc+')] opacity-20"></div>
                                    <div className="flex items-center justify-center gap-6 relative z-10">
                                        <div className="flex flex-col items-center">
                                            <span className="text-[#FFD700] font-medium text-[9px] md:text-xs lg:text-sm uppercase tracking-wider">Open</span>
                                            <span className="text-white font-bold text-[13px] md:text-base lg:text-lg tracking-wide px-3 rounded bg-[#5b3410] border border-[#FFD700]/30">
                                                {convertTo12Hour(startTime) || 'Loading...'}
                                            </span>
                                        </div>
                                        <div className="h-7 md:h-8 lg:h-9 w-px bg-gradient-to-b from-transparent via-[#FFD700]/30 to-transparent"></div>
                                        <div className="flex flex-col items-center">
                                            <span className="text-[#FFD700] font-medium text-[9px] md:text-xs lg:text-sm uppercase tracking-wider">Close</span>
                                            <span className="text-white font-bold text-[13px] md:text-base lg:text-lg tracking-wide px-3 rounded bg-[#5b3410] border border-[#FFD700]/30">
                                                {convertTo12Hour(endTime) || 'Loading...'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                
                        {/* Draw Info Section with compact styling */}
                        <div className="mt-1 mb-1 bg-gradient-to-b from-[#3b2208] to-[#5b3410] rounded-xl border border-[#FFD700]/30 shadow-lg overflow-hidden">
                            <div className="flex divide-x divide-[#FFD700]/20">
                                <div className="p-1 md:p-2 flex-shrink-0 flex-1">
                                    <h3 className="text-[#FFD700] text-[10px] md:text-sm lg:text-base font-bold uppercase tracking-wider mb-1 text-center whitespace-nowrap">AAJ KA SINGLE</h3>
                                    <div className="flex items-center justify-center">
                                        <p className="text-white text-sm md:text-base lg:text-lg font-bold">
                                            {drawInfo.single ? drawInfo.single.split(',').map((num, index) => (
                                                <span key={index} className="inline-block mx-1">{num}</span>
                                            )) : "--"}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-1 md:p-2 flex-1">
                                    <h3 className="text-[#FFD700] text-[10px] md:text-sm lg:text-base font-bold uppercase tracking-wider mb-1 text-center whitespace-nowrap">Patti Panel</h3>
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="flex items-center justify-center">
                                            <p className="text-white text-sm md:text-base lg:text-lg font-bold whitespace-nowrap overflow-hidden">
                                                {drawInfo.panel ? drawInfo.panel.split(',').slice(0, 4).map((num, index) => (
                                                    <span key={index} className="inline-block mx-1 text-[calc(0.7rem+0.3vw)] md:text-base lg:text-lg">{num}</span>
                                                )) : "--"}
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <p className="text-white text-sm md:text-base lg:text-lg font-bold whitespace-nowrap overflow-hidden">
                                                {drawInfo.panel ? drawInfo.panel.split(',').slice(4, 8).map((num, index) => (
                                                    <span key={index} className="inline-block mx-1 text-[calc(0.7rem+0.3vw)] md:text-base lg:text-lg">{num}</span>
                                                )) : ""}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-1 md:p-2 flex-1">
                                    <h3 className="text-[#FFD700] text-[10px] md:text-sm lg:text-base font-bold uppercase tracking-wider mb-1 text-center whitespace-nowrap">Jodi Dhamaka</h3>
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="flex items-center justify-center">
                                            <p className="text-white text-sm md:text-base lg:text-lg font-bold whitespace-nowrap overflow-hidden">
                                                {drawInfo.jodi ? drawInfo.jodi.split(',').slice(0, 3).map((num, index) => (
                                                    <span key={index} className="inline-block mx-1 text-[calc(0.7rem+0.3vw)] md:text-base lg:text-lg">{num}</span>
                                                )) : "--"}
                                            </p>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <p className="text-white text-sm md:text-base lg:text-lg font-bold whitespace-nowrap overflow-hidden">
                                                {drawInfo.jodi ? drawInfo.jodi.split(',').slice(3, 6).map((num, index) => (
                                                    <span key={index} className="inline-block mx-1 text-[calc(0.7rem+0.3vw)] md:text-base lg:text-lg">{num}</span>
                                                )) : ""}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sticky days bar - now part of the sticky header container */}
                <div className="bg-[#e5a97d] shadow-lg border-b-2 border-[#5b3410]">
                    <div className={`${pattern === "d" ? sevenDaysReturn.innerDiv : numberOfWeeksToShowAndDaysToShow().innerDiv} py-2`}>
                        {(pattern === "d" ? ["Date", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] : numberOfWeeksToShowAndDaysToShow().daysArray).map((day, index) => (
                            <div
                                key={day}
                                className="font-bold text-xs sm:text-sm md:text-sm lg:text-base text-[#5b3410] flex justify-center items-center"
                            >
                                {day}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className={`${pattern === "d" ? sevenDaysReturn.mainDiv : numberOfWeeksToShowAndDaysToShow().mainDiv} mt-2`}>
                {Object.keys(groupedDraws).map((weekStartString, weekIndex) => (
                    <div key={weekStartString} className={`border-t border-[#5b3410]/60 ${pattern === "d" ? sevenDaysReturn.innerDiv : numberOfWeeksToShowAndDaysToShow().innerDiv} bg-[#ffcc99]`}>
                        <div className="col-span-1 text-[9px] sm:pl-2 text-black font-bold md:text-base lg:text-2xl py-2 sm:py-6 flex flex-col items-center">
                            <div className="text-[8px] sm:text-base md:text-lg lg:text-2xl">{weekStartString}</div>
                            <div>
                                <svg className="w-4" xmlns="http://www.w3.org/2000/svg" fill="#5b3410" viewBox="0 0 256 256">
                                    <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"></path>
                                </svg>
                            </div>
                            <div className="text-[8px] sm:text-base md:text-lg lg:text-2xl">{getEndOfWeek(weekStartString)}</div>
                        </div>
                        {groupedDraws[weekStartString].map((draw, drawIndex) => (
                            <div
                                key={draw.sortDate.toLocaleDateString()}
                                className={`flex border-l border-[#5b3410]/60 items-center sm:py-5 justify-center hover:bg-[#e5a97d] transition-colors duration-200`}
                            >
                                <div className={`flex justify-evenly ${pattern === "d" ? "" : hideColumnsForDraw(drawIndex, drawIndex)}`}>
                                    {(jodi === "false" && pattern === "m") && (
                                        <div className={`flex flex-col text-[11px] font-extrabold sm:text-3xl ${isSpecialNumber(getMiddlePatti(draw.openRoundPatti, draw.closeRoundPatti)) ? 'text-red-600' : 'text-black'}`}>
                                            {draw.openRoundPatti
                                                ? draw.openRoundPatti.split('').map((digit, index) => (
                                                    <span key={index}>{digit}</span>
                                                ))
                                                : Array.from({length: 3}, (_, index) => (
                                                    <span key={index}>*</span>
                                                ))}
                                        </div>
                                    )}
                                    {(pattern === "m") && (
                                        <div className="md:mx-2 p-1 text-sm font-black lg:text-7xl md:text-3xl flex flex-col justify-center lg:p-4">
                                            <span className={`inline-block ${isSpecialNumber(getMiddlePatti(draw.openRoundPatti, draw.closeRoundPatti)) ? 'text-red-600' : 'text-black'}`}>
                                                {getMiddlePatti(draw.openRoundPatti, draw.closeRoundPatti)}
                                            </span>
                                        </div>
                                    )}
                                    {(pattern === "d") && (
                                        <div className="lg:mx-4 text-sm lg:text-7xl md:text-3xl font-bold flex flex-col justify-center lg:p-4 transition-all duration-200 hover:scale-110">
                                            <span className="inline-block text-transparent bg-gradient-to-b from-purple-900 to-pink-900 bg-clip-text drop-shadow-[0_0_8px_rgba(168,85,247,0.5)]" style={{WebkitBackgroundClip: 'text'}}>
                                                {draw.number}
                                            </span>
                                        </div>
                                    )}
                                    {(jodi === "false" && pattern === "m") && (
                                        <div className={`flex flex-col text-[11px] font-extrabold sm:text-3xl ${isSpecialNumber(getMiddlePatti(draw.openRoundPatti, draw.closeRoundPatti)) ? 'text-red-600' : 'text-black'}`}>
                                            {draw.closeRoundPatti
                                                ? draw.closeRoundPatti.split('').map((digit, index) => (
                                                    <span key={index} className="mr-1">{digit}</span>
                                                ))
                                                : Array.from({length: 3}, (_, index) => (
                                                    <span key={index} className="mr-1">*</span>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>

        </div>
    );
}

// We're not using this function for colors anymore, but keeping it for any other references
export const getMiddlePattiColorClass = (middlePatti) => {
    const numbersToCheck = ["16", "27", "38", "49", "50", "61", "72", "83", "94", "05", "11", "22", "33", "44", "55", "66", "77", "88", "99", "00"];
    const containsMatchingNumber = numbersToCheck.some(number => middlePatti.includes(number));
    return containsMatchingNumber ? "text-[#82541b] font-bold animate-pulse" : "text-[#00094d] font-semibold";
};

const isSpecialNumber = (number) => {
    const specialNumbers = ["16", "27", "38", "49", "50", "61", "72", "83", "94", "05", 
                          "11", "22", "33", "44", "55", "66", "77", "88", "99", "00"];
    return specialNumbers.includes(number);
};

const isSpecialPatti = (patti) => {
    // Add special patti combinations that correspond to special jodi numbers
    const specialPattis = [
        "157", "267", "378", "489", "590", 
        "168", "279", "380", "491", "158",
        "269", "370", "481", "159", "260",
        "371", "482", "159", "260", "371",
        "111", "222", "333", "444", "555",
        "666", "777", "888", "999", "000"
    ];
    return specialPattis.includes(patti);
};

export const getMiddlePatti = (open, close) => {
    const openNumber = parseInt(open, 10);
    const closeNumber = parseInt(close, 10);

    const openDigits = isNaN(openNumber) ? [] : openNumber.toString().split('').map(Number);
    const closeDigits = isNaN(closeNumber) ? [] : closeNumber.toString().split('').map(Number);

    const openSum = openDigits.reduce((a, b) => a + b, 0);
    const closeSum = closeDigits.reduce((a, b) => a + b, 0);

    const openLastDigit = openDigits.length > 0 ? openSum.toString().slice(-1) : '*';
    const closeLastDigit = closeDigits.length > 0 ? closeSum.toString().slice(-1) : '*';

    if (openDigits.length > 0 && closeDigits.length > 0) {
        return `${openLastDigit}${closeLastDigit}`;
    } else if (openDigits.length > 0) {
        return `${openLastDigit}*`;
    } else if (closeDigits.length > 0) {
        return `*${closeLastDigit}`;
    } else {
        return '**';
    }
};
